import React, { useEffect, useRef } from "react";

interface IImagePath {
  url: string;
  time_tag: string;
}

interface Props {
  imagePaths: IImagePath[];
  animationDelay: number;
}

const CanvasAnimation: React.FC<Props> = ({ imagePaths, animationDelay }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const currentImageIndexRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");

    if (!canvas || !ctx) return;

    const loadImage = (path: string) => {
      return new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = (error) => reject(new Error(`Error loading image: ${path}`));
        image.src = "https://services.swpc.noaa.gov" + path;
      });
    };

    const animate = () => {
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const currentImage = images[currentImageIndexRef.current];
      ctx.drawImage(currentImage, 0, 0, canvas.width, canvas.height);
      currentImageIndexRef.current = (currentImageIndexRef.current + 1) % images.length;
      setTimeout(animate, animationDelay);
    };

    let images: HTMLImageElement[] = [];

    const loadImages = async () => {
      try {
        const imagePromises = imagePaths.map((path) => loadImage(path.url));
        images = await Promise.all(imagePromises);

        // Resize canvas to match image dimensions
        const currentImage = images[0];
        canvas.width = currentImage.width;
        canvas.height = currentImage.height;

        animate();
      } catch (error) {
        console.error("Error loading images:", error);
      }
    };

    loadImages();

    return () => {
      images.forEach((image) => URL.revokeObjectURL(image.src));
    };
  }, [imagePaths, animationDelay]);

  return <canvas ref={canvasRef} className="w-[100%] mb-1" />;
};

export default CanvasAnimation;
